import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { FileText } from 'lucide-react';
import { EntityTile, type EntityTileProps } from '../EntityTile';
import './EntityFeed.css';

/** Data for a single feed item — EntityTile props minus accordion state (managed by feed). */
export interface EntityFeedItem extends Omit<EntityTileProps, 'isExpanded' | 'onToggle' | 'isEvenRow'> {
  /** Unique item ID (used for accordion state) */
  id: string;
  /** Optional group key — items with the same key are grouped together */
  group?: string;
}

export interface EntityFeedGroup {
  key: string;
  label: string;
}

export interface EntityFeedProps {
  /** Items to display */
  items: EntityFeedItem[];
  /** Optional class name */
  className?: string;
  /** Custom icon for the empty state (defaults to FileText) */
  emptyIcon?: ReactNode;
  /** Empty state message */
  emptyMessage?: string;
  /** Group definitions — when provided, items are grouped by their `group` field */
  groups?: EntityFeedGroup[];
  /** Initial expanded item ID (deep-link support) */
  initialExpandedId?: string;
  /** Fires when accordion state changes (deep-link support) */
  onExpandedChange?: (id: string | null) => void;
}

/**
 * EntityFeed — Generic feed component for any farm entity type.
 * Manages single-item accordion state, optional grouping, and alternating row colors.
 * Follows the same visual pattern as EventFeed.
 */
export const EntityFeed: React.FC<EntityFeedProps> = ({
  items,
  className = '',
  emptyIcon,
  emptyMessage = 'Keine Einträge vorhanden',
  groups,
  initialExpandedId,
  onExpandedChange,
}) => {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(initialExpandedId ?? null);
  const containerRef = useRef<HTMLDivElement>(null);
  const didScrollRef = useRef(false);

  const handleToggle = (id: string) => {
    const next = expandedItemId === id ? null : id;
    setExpandedItemId(next);
    onExpandedChange?.(next);
  };

  // Scroll initial expanded item into view after first render
  useEffect(() => {
    if (!initialExpandedId || didScrollRef.current) return;
    didScrollRef.current = true;

    // Use rAF to ensure the DOM has painted the expanded tile
    requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector(`[data-entity-id="${initialExpandedId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [initialExpandedId]);

  const hasItems = items.length > 0;

  // Track global index for alternating row colors
  let globalIndex = 0;

  const renderTile = (item: EntityFeedItem) => {
    const { id, group: _group, ...tileProps } = item;
    const isEven = globalIndex % 2 === 0;
    globalIndex++;

    return (
      <div key={id} data-entity-id={id}>
        <EntityTile
          {...tileProps}
          isExpanded={expandedItemId === id}
          onToggle={() => handleToggle(id)}
          isEvenRow={isEven}
        />
      </div>
    );
  };

  const renderGrouped = () => {
    if (!groups) return null;

    return groups.map((grp) => {
      const groupItems = items.filter((item) => item.group === grp.key);
      if (groupItems.length === 0) return null;

      return (
        <div key={grp.key} className="entity-feed__group">
          <div className="entity-feed__group-header">
            <span className="entity-feed__group-label">{grp.label}</span>
            <span className="entity-feed__group-line" />
          </div>
          <div className="entity-feed__items">
            {groupItems.map(renderTile)}
          </div>
        </div>
      );
    });
  };

  const renderFlat = () => {
    return (
      <div className="entity-feed__items">
        {items.map(renderTile)}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`entity-feed ${className}`}>
      {hasItems ? (
        groups ? renderGrouped() : renderFlat()
      ) : (
        <div className="entity-feed__empty">
          {emptyIcon || <FileText className="entity-feed__empty-icon" />}
          <p className="entity-feed__empty-message">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
};

export default EntityFeed;
